import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import type { DatasetResource } from "@prequel/import-react";
import { useDataset } from "@prequel/import-react";
import {
  Button,
  ButtonStyle,
  checkTabIsActive,
  IdValue,
  MenuItemStyle,
  Modal,
  Spinner,
} from "@prequel/shared-components";

import { useRunExtract } from "@/api/import/datasets";
import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import RunExtractModal from "@/pages/import/DatasetsPage/RunExtractModal";
import { useToast } from "@/providers/ToastProvider";

export type DatasetContext = {
  dataset: DatasetResource;
  refreshDataset: () => void;
  extractsRefreshKey?: number;
};

export function useDatasetContext(): DatasetContext {
  return useOutletContext<DatasetContext>();
}

const DatasetPage = () => {
  const datasetID = useRequiredParam("datasetID");
  const { addToast } = useToast();
  const { data: dataset, error, isLoading, refetch: refreshDataset } = useDataset(datasetID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load dataset." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!dataset) {
    return <Navigate to="/import/datasets" replace />;
  }

  return <Outlet context={{ dataset, refreshDataset }} />;
};

export const DatasetDetailsPage = () => {
  const navigate = useNavigate();
  const { dataset, refreshDataset } = useDatasetContext();
  const runExtract = useRunExtract();
  const [showCustomExtract, setShowCustomExtract] = useState(false);
  const [extractsRefreshKey, setExtractsRefreshKey] = useState(0);
  const basePath = `/import/datasets/${dataset.id}`;
  const title = dataset.name;

  const submitExtract = (recordIDs?: string[]) =>
    runExtract.mutate(
      { id: dataset.id, recordIDs },
      {
        onSuccess: () => {
          setShowCustomExtract(false);
          setExtractsRefreshKey((key) => key + 1);
          navigate(`${basePath}/extracts`);
        },
      },
    );

  const tabs = [
    <NavLink key="Overview" to={basePath} className={checkTabIsActive} end>
      Overview
    </NavLink>,
    <NavLink key="Extracts" to={`${basePath}/extracts`} className={checkTabIsActive} end>
      Extracts
    </NavLink>,
    <NavLink key="Record Debug" to={`${basePath}/records`} className={checkTabIsActive} end>
      Record Debug
    </NavLink>,
    <NavLink key="Admin" to={`${basePath}/admin`} className={checkTabIsActive} end>
      Admin
    </NavLink>,
  ];

  return (
    <DetailPageLayout
      title={title}
      dynamicLabel={title}
      onGoBack={() => navigate("/import/datasets")}
      subheading={
        <div className="flex items-center gap-2">
          <IdValue id={dataset.id} />
        </div>
      }
      tabs={tabs}
      actions={
        <Button
          text="Run Extract"
          type={ButtonStyle.TERTIARY}
          disabled={runExtract.isPending}
          onClick={() => submitExtract()}
          menuItems={[
            {
              label: "Run Custom Extract…",
              style: MenuItemStyle.PRIMARY,
              onClick: () => setShowCustomExtract(true),
            },
          ]}
        />
      }
    >
      <Modal open={showCustomExtract} onClose={() => setShowCustomExtract(false)}>
        <RunExtractModal
          isSubmitting={runExtract.isPending}
          onCancel={() => setShowCustomExtract(false)}
          onSubmit={(recordIDs) => submitExtract(recordIDs)}
        />
      </Modal>
      <Outlet context={{ dataset, refreshDataset, extractsRefreshKey }} />
    </DetailPageLayout>
  );
};

export default DatasetPage;
